export interface GlossaryTerm {
  term: string
  aliases?: string[]
  definition: string
  doNotTranslate?: boolean
  translations?: Record<string, string[]>
  note?: string
}

export interface Glossary {
  terms: GlossaryTerm[]
}

/** Term plus any aliases — every EN surface form that maps to this term. */
export function surfaceForms(t: GlossaryTerm): string[] {
  return t.aliases ? [t.term, ...t.aliases] : [t.term]
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/** Whole-word, case-insensitive matcher for one surface form. */
function formMatches(value: string, form: string): boolean {
  return new RegExp(`\\b${escapeRegExp(form)}\\b`, "i").test(value)
}

/** Terms whose EN surface form appears (word-boundary) in `enValue`. */
export function termsInValue(
  enValue: string,
  glossary: Glossary
): GlossaryTerm[] {
  return glossary.terms.filter((t) =>
    surfaceForms(t).some((form) => formMatches(enValue, form))
  )
}

/** Keys whose EN value contains ≥1 glossary term — feeds reviewTier (F2). */
export function glossaryKeysFor(
  enMessages: Record<string, string>,
  glossary: Glossary
): Set<string> {
  const out = new Set<string>()
  for (const [key, value] of Object.entries(enMessages)) {
    if (termsInValue(value, glossary).length > 0) out.add(key)
  }
  return out
}

/** Approved renderings for a term in a locale (verbatim term if doNotTranslate). */
export function approvedRenderings(t: GlossaryTerm, locale: string): string[] {
  if (t.doNotTranslate) return [t.term]
  return t.translations?.[locale] ?? []
}

/**
 * One row of the internal glossary page: a term plus its per-locale approved
 * renderings and how many EN strings reference it. Shared by the SSO-gated
 * `/api/review/glossary` route and its client renderer so the shape never
 * drifts between producer and consumer.
 */
export interface GlossaryRow {
  term: string
  aliases: string[]
  definition: string
  doNotTranslate: boolean
  renderings: Record<string, string[]>
  references: number
}

export interface GlossaryResponse {
  locales: string[]
  terms: GlossaryRow[]
}
